import { FC } from "react";
import { TableCell, tableCellClasses, TableRow, TextField, styled } from "@mui/material";
import CheckAttr from "../../../utils/CheckAttr";
import { ActionType } from "../../../types/AlertType";
import { ROWTYPE, isValidInput, normaliserArticleSansActes } from "./AddBudgetReforecastForm";
import { MODE_PROD, PROFIL_MANAGER, articlesSansActes } from "../../../constants/Constants";
import { calculCABudget, calculCoutBudget, calculMargeValeurBudget, calculTauxMargeBudget } from "../../../utils/utils";
import { profilsCanManageBudget } from "../../content/budget/Budget";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 11,
        textAlign: 'center'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 11,
        textAlign: 'center'
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0
    },
}));

interface AddCoutRowFormProps {
    profil: string;
    clientMod: string;
    operationMod: string;
    article: ROWTYPE;
    tabRowsError: string[];
    handleChangeRowsForBudget: (row: ROWTYPE, action: ActionType) => void;
};

const formatDmtForTimeInput = (dmt: string | null | undefined): string => {
    if (!dmt) return "";
    const parts = dmt.split(":");
    if (parts.length >= 2) {
        const hh = parts[0].padStart(2, "0");
        const mm = parts[1].padStart(2, "0");
        const ss = parts[2] ? parts[2].padStart(2, "0") : "00";
        return `${hh}:${mm}:${ss}`;
    }
    return dmt;
};

const AddBudgetReforecastRowForm: FC<AddCoutRowFormProps> = ({
    profil,
    clientMod,
    operationMod,
    article,
    tabRowsError,
    handleChangeRowsForBudget
}) => {

    // Heures de management non facturées : Heure CA, Acte, PU Heure et PU Acte non saisis (à 0)
    const sansActes = articlesSansActes.includes(article?.libelle);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        let defaultArticle: ROWTYPE = article;

        const val = value.replace(",", ".");

        switch (name) {
            case 'heure_ca_budget':
                const ca_budget = calculCABudget(val, article?.tarif_heure);
                const marge_valeur_budget = calculMargeValeurBudget(ca_budget, article?.cout_budget);
                const tx_marge_budget = calculTauxMargeBudget(marge_valeur_budget, ca_budget);
                defaultArticle = {
                    ...article,
                    heure_ca_budget: val,
                    ca_budget,
                    marge_valeur_budget,
                    tx_marge_budget
                }
                break;
            case 'acte_budget':
                const ca_budget2 = calculCABudget(val, article?.tarif_acte);
                const marge_valeur_budget2 = calculMargeValeurBudget(ca_budget2, article?.cout_budget);
                const tx_marge_budget2 = calculTauxMargeBudget(marge_valeur_budget2, ca_budget2);
                defaultArticle = {
                    ...article,
                    acte_budget: val,
                    ca_budget: ca_budget2,
                    marge_valeur_budget: marge_valeur_budget2,
                    tx_marge_budget: tx_marge_budget2
                }
                break;
            case 'tarif_heure':
                if (isValidInput(val)) {
                    const ca_budget = calculCABudget(article?.heure_ca_budget, val);
                    const marge_valeur_budget = calculMargeValeurBudget(ca_budget, article?.cout_budget);
                    const tx_marge_budget = calculTauxMargeBudget(marge_valeur_budget, ca_budget);
                    defaultArticle = {
                        ...article,
                        tarif_heure: val,
                        ca_budget,
                        marge_valeur_budget,
                        tx_marge_budget
                    }
                }
                break;
            case 'tarif_acte':
                if (isValidInput(val)) {
                    const ca_budget = calculCABudget(article?.acte_budget, val);
                    const marge_valeur_budget = calculMargeValeurBudget(ca_budget, article?.cout_budget);
                    const tx_marge_budget = calculTauxMargeBudget(marge_valeur_budget, ca_budget);
                    defaultArticle = {
                        ...article,
                        tarif_acte: val,
                        ca_budget,
                        marge_valeur_budget,
                        tx_marge_budget
                    }
                }
                break;
            case 'heure_cout_budget':
                const cout_budget = calculCoutBudget(val, article?.cout_activite);
                const marge_valeur_budget1 = calculMargeValeurBudget(article?.ca_budget, cout_budget);
                const tx_marge_budget1 = calculTauxMargeBudget(marge_valeur_budget1, article?.ca_budget);
                defaultArticle = {
                    ...article,
                    heure_cout_budget: val,
                    cout_budget,
                    marge_valeur_budget: marge_valeur_budget1,
                    tx_marge_budget: tx_marge_budget1
                }
                break;
            case 'cout_activite':
                if (isValidInput(val)) {
                    const cout_budget = calculCoutBudget(article?.heure_cout_budget, val);
                    const marge_valeur_budget = calculMargeValeurBudget(article?.ca_budget, cout_budget);
                    const tx_marge_budget = calculTauxMargeBudget(marge_valeur_budget, article?.ca_budget);
                    defaultArticle = {
                        ...article,
                        cout_activite: val,
                        cout_budget,
                        marge_valeur_budget,
                        tx_marge_budget
                    }
                }
                break;
            case 'taux_occupation':
            case 'taux_conges':
            case 'taux_absenteisme':
                defaultArticle = {
                    ...article,
                    [name]: val !== "" ? val : null
                };
                break;
            case 'dmt':
                defaultArticle = {
                    ...article,
                    dmt: value !== "" ? value : null
                };
                break;
            default:
                defaultArticle = {
                    ...article,
                    [name]: value ?? null
                }
        };

        handleChangeRowsForBudget(normaliserArticleSansActes(defaultArticle), "UPDATE");
    };

    const toDisable = (): boolean => {
        /**
         * En tant que DRC, lorsque je choisit un client existant et opération existant,
         * je ne peux pas saisir des budgets pour les articles existants.
         * Seule les budgets (ca, taux, tarif, cout) de l’article autre que je puisse saisir
         */
        if (profilsCanManageBudget.includes(profil)) {
            if ((article?.is_budget !== 1) && (clientMod === MODE_PROD) && (operationMod === MODE_PROD)) {
                return true;
            }
        }

        return false;
    };

    /**
     * Les champs PU et Cout sont grisés si je suis Manager
     */
    const disableInputOfManager = () => {
        if (profil === PROFIL_MANAGER) {
            return true;
        }

        return false;
    }

    const errorHeureCaBudget = `${article?.id} - 1`;
    const errorActeBudget = `${article?.id} - 2`;
    const errorPUHeure = `${article?.id} - 3`;
    const errorPUActe = `${article?.id} - 4`;
    const errorHeureCoutBudget = `${article?.id} - 5`;
    const errorCoutActivite = `${article?.id} - 6`;

    const disablePrixUnitaireInput = () => {
        let disableHeureInput = false;
        let disableActeInput = false;
        if (article?.code_article_libelle) {
            const lastCharactersOfCodeArticle = article?.code_article_libelle?.slice(-1).toLowerCase();

            disableHeureInput = lastCharactersOfCodeArticle === "a";
            disableActeInput = lastCharactersOfCodeArticle === "h";
        } else {
            disableHeureInput = CheckAttr(article?.tarif_acte);
            disableActeInput = CheckAttr(article?.tarif_heure);
        }

        return {
            disableHeureInput,
            disableActeInput
        }
    };

    const disablePUInput = disablePrixUnitaireInput();

    return (
        <StyledTableRow>
            <StyledTableCell>{article?.bu_libelle}</StyledTableCell>
            <StyledTableCell>{article?.type_libelle}</StyledTableCell>
            <StyledTableCell>{article?.code_article_libelle}</StyledTableCell>
            <StyledTableCell align="center">{article?.libelle}</StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="heure_ca_budget"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.heure_ca_budget}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorHeureCaBudget)}
                    disabled={sansActes || CheckAttr(article?.tarif_acte)}
                    InputLabelProps={{
                        shrink: CheckAttr(article?.heure_ca_budget),
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <TextField
                    type="number"
                    name="acte_budget"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.acte_budget}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorActeBudget)}
                    disabled={sansActes || CheckAttr(article?.tarif_heure)}
                    InputLabelProps={{
                        shrink: CheckAttr(article?.acte_budget),
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <TextField
                    type="number"
                    name="tarif_heure"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.tarif_heure}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorPUHeure)}
                    disabled={
                        sansActes ||
                        disableInputOfManager() ||
                        toDisable() ||
                        disablePUInput?.disableHeureInput ||
                        CheckAttr(article?.tarif_acte)
                    }
                    InputLabelProps={{
                        shrink: CheckAttr(article?.tarif_heure),
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="tarif_acte"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.tarif_acte}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorPUActe)}
                    disabled={
                        sansActes ||
                        disableInputOfManager() ||
                        toDisable() ||
                        disablePUInput?.disableActeInput ||
                        CheckAttr(article?.tarif_heure)
                    }
                    InputLabelProps={{
                        shrink: CheckAttr(article?.tarif_acte)
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                {article?.ca_budget && parseFloat(String(article?.ca_budget))?.toFixed(2)}
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="heure_cout_budget"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.heure_cout_budget}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorHeureCoutBudget)}
                    InputLabelProps={{
                        shrink: CheckAttr(article?.heure_cout_budget)
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="cout_activite"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    sx={{ width: 105 }}
                    value={article?.cout_activite}
                    onChange={handleChange}
                    error={tabRowsError?.includes(errorCoutActivite)}
                    disabled={disableInputOfManager() || toDisable()}
                    InputLabelProps={{
                        shrink: CheckAttr(article?.cout_activite),
                    }}
                    inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                {article?.cout_budget && parseFloat(article?.cout_budget)?.toFixed(2)}
            </StyledTableCell>

            <StyledTableCell>
                {article?.marge_valeur_budget && parseFloat(String(article?.marge_valeur_budget))?.toFixed(2)}
            </StyledTableCell>

            <StyledTableCell>
                {article?.tx_marge_budget && parseFloat(String(article?.tx_marge_budget))?.toFixed(2)}
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    name="dmt"
                    color="warning"
                    variant="standard"
                    size="small"
                    type="time"
                    autoComplete="off"
                    placeholder="HH:MM:SS"
                    sx={{ width: 110 }}
                    value={formatDmtForTimeInput(article?.dmt)}
                    onChange={handleChange}
                    error={tabRowsError?.includes(`${article?.id} - dmt`)}
                    InputLabelProps={{
                        shrink: true
                    }}
                    inputProps={{
                        step: 1
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="taux_occupation"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 85"
                    sx={{ width: 85 }}
                    value={article?.taux_occupation || ""}
                    onChange={handleChange}
                    error={tabRowsError?.includes(`${article?.id} - tx_occ`)}
                    InputLabelProps={{
                        shrink: true
                    }}
                    inputProps={{
                        // step: 0.1,
                        min: 0,
                        max: 100,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="taux_conges"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 5"
                    sx={{ width: 85 }}
                    value={article?.taux_conges || ""}
                    onChange={handleChange}
                    error={tabRowsError?.includes(`${article?.id} - tx_cg`)}
                    InputLabelProps={{
                        shrink: true
                    }}
                    inputProps={{
                        // step: 0.1,
                        min: 0,
                        max: 100,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    type="number"
                    name="taux_absenteisme"
                    color="warning"
                    variant="standard"
                    size="small"
                    autoComplete="off"
                    placeholder="Ex: 3"
                    sx={{ width: 85 }}
                    value={article?.taux_absenteisme || ""}
                    onChange={handleChange}
                    error={tabRowsError?.includes(`${article?.id} - tx_abs`)}
                    InputLabelProps={{
                        shrink: true
                    }}
                    inputProps={{
                        // step: 0.1,
                        min: 0,
                        max: 100,
                        lang: 'en-US'
                    }}
                />
            </StyledTableCell>
        </StyledTableRow>
    );
};

export default AddBudgetReforecastRowForm;